import { BaseApiService } from './BaseApiService';

// Production Line interfaces
export interface ProductionLine {
  id: number;
  name: string;
  description?: string;
  defaultWorkers: number;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductionLineData {
  name: string;
  description?: string;
  default_workers?: number;
  is_active?: boolean;
  display_order?: number;
}

export interface UpdateProductionLineData {
  name?: string;
  description?: string;
  default_workers?: number;
  is_active?: boolean;
  display_order?: number;
}

export interface ProductionLineWithCapacity extends ProductionLine {
  current_utilization: number;
  total_orders: number;
}

export interface UpdateWorkersData {
  workers: number;
}

class ProductionLineService extends BaseApiService {
  private readonly baseUrl = '/production-lines';

  // Get all production lines
  async getAllProductionLines(): Promise<ProductionLine[]> {
    return this.get<ProductionLine[]>(this.baseUrl);
  }

  // Get active production lines only
  async getActiveProductionLines(): Promise<ProductionLine[]> {
    return this.get<ProductionLine[]>(`${this.baseUrl}/active`);
  }

  // Get production lines with workload information
  async getProductionLinesWithWorkload(): Promise<ProductionLineWithCapacity[]> {
    return this.get<ProductionLineWithCapacity[]>(`${this.baseUrl}/with-workload`);
  }

  // Get specific production line
  async getProductionLineById(id: number): Promise<ProductionLine> {
    return this.get<ProductionLine>(`${this.baseUrl}/${id}`);
  }

  // Get production line with capacity information
  async getProductionLineCapacity(id: number): Promise<ProductionLineWithCapacity> {
    return this.get<ProductionLineWithCapacity>(`${this.baseUrl}/${id}/capacity`);
  }

  // Create new production line
  async createProductionLine(data: CreateProductionLineData): Promise<ProductionLine> {
    return this.post<ProductionLine>(this.baseUrl, data);
  }

  // Update production line
  async updateProductionLine(id: number, data: UpdateProductionLineData): Promise<ProductionLine> {
    return this.put<ProductionLine>(`${this.baseUrl}/${id}`, data);
  }

  // Delete (deactivate) production line
  async deleteProductionLine(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  // Activate production line
  async activateProductionLine(id: number): Promise<ProductionLine> {
    return this.post<ProductionLine>(`${this.baseUrl}/${id}/activate`, {});
  }

  // Deactivate production line
  async deactivateProductionLine(id: number): Promise<ProductionLine> {
    return this.post<ProductionLine>(`${this.baseUrl}/${id}/deactivate`, {});
  }

  // Update line workers
  async updateLineWorkers(id: number, workers: number): Promise<ProductionLine> {
    return this.put<ProductionLine>(`${this.baseUrl}/${id}/workers`, { workers });
  }

  // Helper method to get line options for dropdowns
  async getLineOptions(): Promise<Array<{ value: number; label: string; isActive: boolean }>> {
    const lines = await this.getAllProductionLines();
    return lines.map((line) => ({
      value: line.id,
      label: line.name,
      isActive: line.isActive,
    }));
  }

  // Helper method to get active line options only
  async getActiveLineOptions(): Promise<Array<{ value: number; label: string }>> {
    const lines = await this.getActiveProductionLines();
    return lines.map((line) => ({
      value: line.id,
      label: line.name,
    }));
  }

  // Helper method to validate worker count
  validateWorkerCount(workers: number): boolean {
    return workers > 0 && workers <= 50 && Number.isInteger(workers);
  }

  // Helper method to format capacity percentage
  formatCapacityPercentage(percentage: number): string {
    return `${Math.round(percentage)}%`;
  }

  // Helper method to get capacity status color
  getCapacityStatusColor(percentage: number): 'success' | 'warning' | 'danger' {
    if (percentage < 70) return 'success';
    if (percentage < 90) return 'warning';
    return 'danger';
  }

  // Helper method to format line display name with worker count
  formatLineDisplayName(line: ProductionLine): string {
    return `${line.name} (${line.defaultWorkers} workers)`;
  }
}

// Export singleton instance
export const productionLineService = new ProductionLineService();
export default productionLineService;
